import Axios from 'axios';
import { PrintService, PrintDevice } from '@/services/PrintService';

/**
 * Printing the formula worksheet for a compound batch — the batch record with its potency line,
 * steps as performed and sign-offs — from the batch verification and history pages.
 *
 * The backend regenerates the document on every call from the batch's own snapshot and answers
 * with the same shape as the other document prints: with `localPrint` it returns signed URLs the
 * Scriptly Client Service fetches and prints on the chosen local printer; otherwise it sends the
 * document to the store's print queue, which needs a store (the batch's own unless one is passed).
 */

/** The remembered worksheet printer — shared with the blank worksheet print on the compound drug page. */
export const WORKSHEET_PRINTER_STORAGE_KEY = 'active-worksheet-printer';

export interface WorksheetPrinterState {
    printers: PrintDevice[];
    /** The remembered printer when it is still advertised; null means the store default printer. */
    selected: PrintDevice | null;
}

/** Discovers the local printers and restores the remembered worksheet printer among them. */
export async function loadWorksheetPrinters(): Promise<WorksheetPrinterState> {
    const printers = await PrintService.getDevices();
    const saved = localStorage.getItem(WORKSHEET_PRINTER_STORAGE_KEY);
    const selected = saved ? printers.find(p => p.name === saved) ?? null : null;
    return { printers, selected };
}

export interface PrintCompoundBatchWorksheetOptions {
    batchId: number;
    /** The store whose queue and header the worksheet uses; omitted, the backend uses the batch's own store. */
    storeId?: number | null;
    /** The chosen local printer; null sends the document to the store's print queue. */
    printer: PrintDevice | null;
    /** API base URL for the client service, with any trailing `/api` already stripped. */
    apiBaseUrl?: string;
    /** Auth provider type ("Cognito" vs MSAL/OIDC), read from the Vue instance by the caller. */
    authProviderType?: string;
}

/**
 * Requests the batch worksheet and, for a local printer, hands the returned jobs to the client
 * service. Rethrows on failure so the page can surface the error.
 */
export async function printCompoundBatchWorksheet(options: PrintCompoundBatchWorksheetOptions): Promise<void> {
    const localPrint = !!options.printer;
    const { data } = await Axios.get(`/CompoundBatch/${options.batchId}/formula-worksheet`, {
        params: { localPrint, storeId: options.storeId ?? undefined },
    });

    if (localPrint && data?.toPrint?.length > 0) {
        await PrintService.sendToLocalPrint({
            printer: options.printer!,
            jobs: data.toPrint,
            apiBaseUrl: options.apiBaseUrl,
            authProviderType: options.authProviderType,
            storageKey: WORKSHEET_PRINTER_STORAGE_KEY,
        });
    }
}
